import { motion } from "framer-motion";
import { ArrowUpRight, Instagram, Mail } from "lucide-react";
import Marquee from "@/components/Marquee";
import { MaskedLine, Reveal, SectionTag } from "@/components/Reveal";
import { CONTACT, LINKS } from "@/data";

const Contact = () => (
  <motion.main
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -24 }}
    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    className="bg-ink pt-28 text-paper"
    data-testid="contact-page"
  >
    <header className="px-6 md:px-12">
      <SectionTag no="08" label="CONTACT" dark />
      <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.9] tracking-tighter sm:text-6xl md:text-8xl" data-testid="contact-title">
        <MaskedLine delay={0.1}><span>LET'S MAKE</span></MaskedLine>
        <MaskedLine delay={0.25}><span>SOMETHING <span className="text-ember">MEANINGFUL.</span></span></MaskedLine>
      </h1>
      <Reveal delay={0.3}>
        <p className="mt-8 max-w-xl font-serif text-xl italic leading-relaxed text-paper/60 md:text-2xl">{CONTACT.copy}</p>
      </Reveal>
    </header>

    <section className="px-6 py-16 md:px-12" data-testid="contact-open-to">
      <p className="font-display text-[10px] font-bold uppercase tracking-[0.35em] text-paper/50">OPEN TO</p>
      <div className="mt-6 flex flex-wrap gap-2">
        {CONTACT.openTo.map((o) => (
          <span key={o} className="border border-white/20 px-4 py-2 font-display text-[10px] font-bold uppercase tracking-[0.25em] text-paper/70">
            {o}
          </span>
        ))}
      </div>
    </section>

    <section className="grid border-t border-white/10 md:grid-cols-3" data-testid="contact-ctas">
      {[
        { id: "collaborate", title: "COLLABORATE", desc: "Creative collaboration, youth projects, social campaigns.", href: LINKS.instagram, external: true },
        { id: "business", title: "BUSINESS INQUIRIES", desc: CONTACT.business, href: `mailto:${LINKS.email}`, external: false },
        { id: "connect", title: "LET'S CONNECT", desc: "Networking, ngobrol santai, atau sekadar say hi.", href: LINKS.instagram, external: true },
      ].map((c) => (
        <a
          key={c.id}
          href={c.href}
          target={c.external ? "_blank" : undefined}
          rel={c.external ? "noreferrer" : undefined}
          data-testid={`contact-${c.id}`}
          data-cursor="GO"
          className="group flex min-h-64 flex-col justify-between border-b border-white/10 p-10 transition-colors duration-300 hover:bg-coal md:border-b-0 md:border-r md:last:border-r-0"
        >
          <div>
            <h2 className="font-display text-2xl font-black uppercase tracking-tight md:text-3xl">{c.title}</h2>
            <p className="mt-4 text-sm leading-relaxed text-paper/50">{c.desc}</p>
          </div>
          <ArrowUpRight className="mt-8 h-8 w-8 text-ember transition-transform duration-300 group-hover:translate-x-2 group-hover:-translate-y-2" />
        </a>
      ))}
    </section>

    <section className="border-t border-white/10 px-6 py-20 md:px-12" data-testid="contact-direct">
      <div className="flex flex-col gap-6 md:flex-row md:items-center md:gap-10">
        <a
          href={LINKS.instagram}
          target="_blank"
          rel="noreferrer"
          data-testid="contact-dm-button"
          data-cursor="VISIT"
          className="inline-flex w-fit items-center gap-3 bg-ember px-8 py-5 font-display text-sm font-black uppercase tracking-[0.25em] text-ink transition-transform duration-300 hover:-translate-y-1"
        >
          <Instagram className="h-5 w-5" /> DM @kinhakann
        </a>
        <a
          href={`mailto:${LINKS.email}`}
          data-testid="contact-email-button"
          data-cursor="SAY HI"
          className="inline-flex w-fit items-center gap-3 border border-paper px-8 py-5 font-display text-sm font-black uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-paper hover:text-ink"
        >
          <Mail className="h-5 w-5" /> {LINKS.email}
        </a>
      </div>
    </section>

    <Marquee items={["KEEP EXPLORING", "KEEP CREATING", "KEEP BECOMING"]} dark />

    <section className="px-6 py-28 text-center md:px-12 md:py-40" data-testid="contact-final">
      <h2 className="font-display text-4xl font-black uppercase leading-[0.95] tracking-tighter sm:text-5xl md:text-7xl" data-testid="final-title">
        <MaskedLine delay={0.1}><span>{CONTACT.finalTitle}</span></MaskedLine>
      </h2>
      <Reveal delay={0.2}>
        <p className="mx-auto mt-10 max-w-2xl text-sm leading-relaxed text-paper/60 md:text-base">{CONTACT.finalText}</p>
        <p className="mt-12 font-display text-xs font-bold uppercase tracking-[0.4em] text-ember" data-testid="final-question">
          {CONTACT.finalQuestion}
        </p>
      </Reveal>
    </section>
  </motion.main>
);

export default Contact;
